var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useEffect, useMemo, useRef } from 'react';
import { useResourceContext, useGetIdentity, useNotify, Translate, useCloseNotification, useRecordContext, } from 'react-admin';
import { SnackbarContent } from '@mui/material';
import { useLock, useUnlock } from '../dataProvider';
import { useAsyncEffect } from './useAsyncEffect';
/**
 * Hook used to put a lock on a record at mount, and unlock the record at unmount.
 * The current resource and recordId are guessed from the context (or the route) if not provided.
 *
 * @param {UseLockOnMountParams} params The lock parameters
 *        { identity, resource, id, meta, lockMutationOptions, unlockMutationOptions }
 * @param {string} params.identity The identity of the locker
 * @param {string} params.resource The resource to lock (guessed from context if not provided)
 * @param {Identifier} params.id The id of the record to lock (guessed from context or route if not provided)
 * @param {any} params.meta An object containing metadata that will be forwarded to the dataProvider
 * @param {Object} params.lockMutationOptions Options forwarded to react-query for the lock mutation
 * @param {Object} params.unlockMutationOptions Options forwarded to react-query for the unlock mutation
 *
 * @returns The current lock and mutation state. Destructure as `{ isLocked, error, isLoading, isLocking }`.
 *
 * @example
 * import { useLockOnMount } from '@react-admin/ra-realtime';
 *
 * const IsLockedByBe = () => {
 *     const { isLocked } = useLockOnMount();
 *     return isLocked
 *         ? <span>You are locking this record</span>
 *         : <span>Lock could not be acquired</span>;
 * };
 *
 * @example
 * const { isLocked, isLoading, error } = useLockOnMount({
 *     identity: 'mario',
 *     resource: 'posts',
 *     id: 123,
 * });
 */
export var useLockOnMount = function (params) {
    if (params === void 0) { params = {}; }
    var identityProp = params.identity, paramsResource = params.resource, paramsId = params.id, meta = params.meta, lockMutationOptions = params.lockMutationOptions, unlockMutationOptions = params.unlockMutationOptions;
    var resource = useResourceContext({ resource: paramsResource });
    var _a = useGetIdentity(), identityFromAuthProvider = _a.identity, isGetIdentityLoading = _a.isLoading;
    var identity = identityProp !== null && identityProp !== void 0 ? identityProp : identityFromAuthProvider === null || identityFromAuthProvider === void 0 ? void 0 : identityFromAuthProvider.id;
    var notify = useNotify();
    // We use this instead of useGetRecordId as a workaround:
    // When using useGetRecordId, the id is first extracted from the URL, then updated when the record is loaded.
    // This caused an additional rerender unlocking the record.
    var contextRecord = useRecordContext();
    var id = paramsId !== null && paramsId !== void 0 ? paramsId : contextRecord === null || contextRecord === void 0 ? void 0 : contextRecord.id;
    var _b = useLock(undefined, undefined, lockMutationOptions), lock = _b[0], lockMutation = _b[1];
    var _c = useUnlock(undefined, undefined, unlockMutationOptions), unlock = _c[0], unlockMutation = _c[1];
    var unlockRequested = useRef(false);
    var _d = useAsyncEffect(function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (id == undefined)
                        return [2 /*return*/, false];
                    if (unlockRequested.current)
                        return [2 /*return*/, false];
                    if (isGetIdentityLoading)
                        return [2 /*return*/, false];
                    if (!identity) {
                        throw new Error('The AuthProvider must have a getIdentity function for locks to work or you must provide the identity option');
                    }
                    return [4 /*yield*/, lockMutation.mutateAsync({
                            resource: resource,
                            params: { identity: identity, id: id, meta: meta },
                        })];
                case 1:
                    _a.sent();
                    return [2 /*return*/, true];
            }
        });
    }); }, function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (id == undefined)
                        return [2 /*return*/];
                    if (isGetIdentityLoading || !identity) {
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, unlockMutation.mutateAsync({
                            resource: resource,
                            params: { identity: identity, id: id, meta: meta },
                        })];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [resource, id, meta, identity, isGetIdentityLoading, lock, unlock]), isLocked = _d.result, error = _d.error, isLoading = _d.isLoading;
    useEffect(function () {
        if (id == undefined)
            return;
        var handleBeforeUnload = function (event) {
            if (!isLocked || !identity || unlockRequested.current)
                return;
            unlockRequested.current = true;
            event.preventDefault();
            // Prevent the tab from closing for older browsers
            event.returnValue = 'unlocking...';
            notify(_jsx(UnlockingNotification, { resource: resource, id: id, meta: meta, identity: identity, unlockMutationOptions: unlockMutationOptions }), {
                autoHideDuration: null,
            });
            // Prevent the tab from closing for older browsers
            return 'unlocking...';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return function () {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [
        id,
        identity,
        isLocked,
        meta,
        notify,
        resource,
        unlock,
        unlockMutationOptions,
    ]);
    var result = useMemo(function () { return ({
        isLocked: isLocked,
        error: error,
        isLoading: isLoading,
        isLocking: isLoading,
        isPending: isLoading,
    }); }, [isLocked, isLoading, error]);
    return result;
};
var UnlockingNotification = forwardRef(function UnlockingNotification(_a, ref) {
    var resource = _a.resource, id = _a.id, meta = _a.meta, identity = _a.identity, unlockMutationOptions = _a.unlockMutationOptions;
    var closeNotification = useCloseNotification();
    var _b = useUnlock(undefined, undefined, __assign(__assign({}, unlockMutationOptions), { onSuccess: function (data, variables, context) {
            if (unlockMutationOptions === null || unlockMutationOptions === void 0 ? void 0 : unlockMutationOptions.onSuccess) {
                unlockMutationOptions.onSuccess(data, variables, context);
            }
            closeNotification();
        } })), unlock = _b[0], isPending = _b[1].isPending;
    useEffect(function () {
        if (isPending)
            return;
        unlock(resource, {
            id: id,
            meta: meta,
            identity: identity,
        });
    }, [resource, id, meta, identity, isPending, unlock]);
    return (_jsx(SnackbarContent, { ref: ref, message: _jsx(Translate, { i18nKey: "ra-realtime.notification.record.unlocking", children: "Unlocking the record..." }) }));
});
/**
 * @deprecated use useLockOnMount instead
 */
export var useLockRecord = useLockOnMount;
